import React, { useCallback, useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { map, switchMap, toArray } from 'rxjs/operators';
import { forkJoin, range } from 'rxjs';
import * as yup from 'yup';
import { useQuery } from '../../../hooks/use-query';
import { route } from '../../../services/utils';
import { useService } from '../../../services/services';

import Form, { SubmitType } from '../../../components/form';
import Button from '../../../components/form/elements/button';
import Link from '../../../components/form/elements/link';
import Text from '../../../components/form/elements/text';
import TextArea from '../../../components/form/elements/text-area';
import Amount from '../../../components/form/elements/amount';
import Select, {
  emptySelectOption,
  Option,
} from '../../../components/form/elements/select';
import Watch from '../../../components/form/elements/watch';
import Disabled from '../../../components/form/elements/disabled';
import CropSelect from './crop-select';
import ProductionTypeSelect from './production-type-select';

import Paginate from '../../../interfaces/paginate';
import { Product } from '../../../interfaces/product';
import ClassifierValue from '../../../interfaces/classifierValue';

import ClassifierPropertyKey from '../../../enums/classifier-property-key';
import ClassifierGroup from '../../../enums/classifier-group';
import Classifier from '../../../enums/classifier';

import css from './create.module.css';
import {
  CROP_CHANGE_PLAN_INDEX,
  CROP_CHANGE_PLAN_VIEW,
} from '../../../router/routes';
import i18n from 'i18next';

const emptyProductionTypeOption = {
  production_type: {
    ...emptySelectOption,
    units: [] as any,
  },
  yield: '',
  yield_unit: emptySelectOption,
};

export default function CropChangePlanCreate() {
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const { classifierService, cropChangePlanService, fieldService } =
    useService();
  const history = useHistory();
  const [params] = useQuery();

  const [defaultValues, setDefaultValues] = useState({
    crop_group_id: null,
    crop_id: null,
    breed: null,
    intercrop_crop_group_id: emptySelectOption,
    intercrop_crop_id: emptySelectOption,
    intercrop_breed: emptySelectOption,
    interculture_crop_group_id: emptySelectOption,
    interculture_crop_id: emptySelectOption,
    interculture_breed: emptySelectOption,
    production_types: [emptyProductionTypeOption],
  });

  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.crop_change_plan.create.';
  const tp = useCallback(
    (key: string, params?: any) => t(transKeyPrefix.concat(key), params),
    [t]
  );

  const checkFieldExistance = useCallback(
    (selectedFields: any, selectedSeasons: any) => {
      // recursively go through field descendants to check if the same field exists in given season
      const findFieldInSeason = (field: any, seasonId: any): any | null => {
        if (field.season_id === seasonId && field.active) {
          return field;
        } else if (field.descendants?.length > 0) {
          return findFieldInSeason(field.descendants[0], seasonId);
        } else {
          return null;
        }
      };

      let fieldErrorText = '';

      if (selectedSeasons && selectedFields) {
        selectedFields.forEach((field: any) => {
          selectedSeasons.forEach((season: any) => {
            const fieldInSelectedSeason = findFieldInSeason(
              field,
              parseInt(season.value)
            );

            if (!fieldInSelectedSeason) {
              fieldErrorText += tp('field_error_text', {
                fieldName: field.name,
                seasonId: season.value,
              });
            }
          });
        });
      }

      return fieldErrorText;
    },
    [tp]
  );

  const schema = yup.object().shape({
    fields: yup
      .array()
      .when('seasons', {
        is: (val: any) => val && val.length > 0,
        then: yup.array().test((fields: any, context: any) => {
          const errorMessage = checkFieldExistance(
            fields,
            context.parent.seasons
          );

          if (errorMessage) {
            return context.createError({
              message: tp('field_error') + errorMessage,
            });
          }

          return true;
        }),
      })
      .required()
      .of(
        yup.object().shape({
          value: yup.string().required(),
        })
      )
      .min(1),
    area: yup.string(),
    seasons: yup
      .array()
      .required()
      .of(
        yup.object().shape({
          value: yup.string().required(),
        })
      )
      .min(1),
    crop_group_id: yup.object().required().shape({
      value: yup.string().required().uuid(),
    }),
    crop_id: yup.object().required().shape({
      value: yup.string().uuid().required(),
    }),
    breed: yup
      .object()
      .nullable()
      .shape({
        value: yup.string().when('breed', {
          is: (breed) => breed !== null && breed !== undefined,
          then: yup.string().required(),
        }),
      })
      .test((breed: any, context: any) => {
        return validateDistinctBreed(context);
      }),
    production_types: yup.array().of(
      yup.object().shape({
        production_type: yup.object().shape({
          label: yup.string().nullable(),
          value: yup.string().nullable(),
        }),
        yield: yup.string().nullable(),
        yield_unit: yup
          .object()
          .nullable()
          .shape({
            label: yup.string(),
          })
          .nullable(),
      })
    ),
    notes: yup.string(),
    // starpkultūraugs
    interculture_breed: yup
      .object()
      .shape({
        label: yup.string().nullable(),
        value: yup.string().nullable(),
      })
      .test((intercultureBreed: any, context: any) => {
        return validateDistinctBreed(context);
      }),
    intercrop_breed: yup
      .object()
      .shape({
        label: yup.string().nullable(),
        value: yup.string().nullable(),
      })
      .test((intercropBreed: any, context: any) => {
        return validateDistinctBreed(context);
      }),
  });

  const submit = useCallback<SubmitType<any>>(
    (form) => {
      form.fields = form.fields.map((field: any) => field.value);
      form.seasons = form.seasons.map((season: any) => season.value);
      form.crop_group_id = form.crop_group_id?.value;
      form.crop_id = form.crop_id?.value;
      form.breed = form.breed?.value;

      form.production_types = form.production_types.map(
        (productionTypeInfo: any) => {
          if (productionTypeInfo?.production_type?.value) {
            return {
              type_id: productionTypeInfo.production_type?.value,
              type_name: productionTypeInfo.production_type?.label,
              type: productionTypeInfo.productin_type?.type,
              yield: productionTypeInfo.yield,
              yield_unit: productionTypeInfo.yield_unit?.label,
            };
          } else {
            return null;
          }
        }
      );

      form.intercrop_crop_group_id = form.intercrop_crop_group_id?.value;
      form.intercrop_crop_id = form.intercrop_crop_id?.value;
      form.intercrop_breed = form.intercrop_breed?.value;

      form.interculture_crop_group_id = form.interculture_crop_group_id?.value;
      form.interculture_crop_id = form.interculture_crop_id?.value;
      form.interculture_breed = form.interculture_breed?.value;

      return [
        cropChangePlanService.store(farmId, form),
        (response) => {
          history.push(
            route(CROP_CHANGE_PLAN_VIEW.concat('?fieldIds=:fieldIds'), {
              farmId: response.cropChangePlan.farm_id,
              season: response.cropChangePlan.season_id,
              id: response.cropChangePlan.id,
              fieldIds: response.fieldIds,
            })
          );
        },
      ];
    },
    [history, farmId, cropChangePlanService]
  );

  useEffect(() => {
    if (params['fieldIds'] && !defaultValues.hasOwnProperty('fields')) {
      const fieldIds = params['fieldIds']?.split(',');

      const subscription = range(0, fieldIds.length)
        .pipe(
          map((index) =>
            fieldService.show(farmId, fieldIds[index], {
              loadDescendants: true,
            })
          ),
          toArray(),
          switchMap((array) => forkJoin(array))
        )
        .subscribe((response) => {
          const fields = response.map((field: any) => {
            return {
              label: `${field.lad_number}, ${field.name}, ${field.area} ha`,
              value: field.id,
              area: field.area,
              lad_number: field.lad_number,
              name: field.name,
              season_id: field.season_id,
              descendants: field.descendants,
              active: field.active,
            } as Option;
          });

          setDefaultValues(() => {
            return { ...defaultValues, fields: fields };
          });
        });
      return () => subscription.unsubscribe();
    }
  }, [farmId, season, fieldService, defaultValues, params]);

  const [body] = useState(() => [
    Watch(
      Select('fields', {
        asyncOptions: (value, loadedOptions, { page, getValues }) =>
          cropChangePlanService
            .index(farmId, season, {
              page,
              withoutCCP: 'yes',
            })
            .pipe(
              map((response: Paginate<any>) => {
                return {
                  hasMore: response.last_page > response.current_page,
                  options: response.data.map((v) => ({
                    label: `${v.lad_number}, ${v.name}, ${v.area} ha`,
                    value: v.id,
                    area: v.area,
                    lad_number: v.lad_number,
                    name: v.name,
                    season_id: v.season_id,
                    descendants: v.descendants,
                    active: v.active,
                  })),
                  additional: { page: response.current_page + 1 },
                };
              })
            ),
        multi: true,
        label: tp('field_names'),
      }),
      ({ value, setValue, getValues }) => {
        let totalFieldArea = 0;

        value
          .filter((field: any) => field.area !== 0)
          .forEach((field: any) => (totalFieldArea += field.area));

        setValue('area', totalFieldArea);
      }
    ),
    Disabled(Text('area', tp('area'))),
    Select('seasons', {
      asyncOptions: (value, loadedOptions, { page, getValues }) =>
        cropChangePlanService.getAvailableSeasons(farmId, season).pipe(
          map((response: Paginate<any>) => {
            return {
              hasMore: response.last_page > response.current_page,
              options: response.data.map((v) => ({
                label: v.title,
                value: v.id,
              })),
              additional: { page: response.current_page + 1 },
            };
          })
        ),
      multi: true,
      label: tp('seasons'),
    }),
    Watch(
      Select('crop_group_id', {
        asyncOptions: (value, loadedOptions) => {
          return cropChangePlanService.productCategoriesWithTypes(farmId).pipe(
            map((response: any) => ({
              options: Object.values(response)
                .map((v: any) => ({
                  label: v.cropGroup.label,
                  value: v.cropGroup.id,
                  product_ids: v.product_ids,
                }))
                .filter((value: any) => value.label !== 'Neattiecas'),
            }))
          );
        },
        multi: false,
        clearable: true,
        label: tp('crop_category'),
      }),
      ({ value, setValue, getValues, trigger }) => {
        const crop = getValues('crop_id');
        const breed = getValues('breed');

        if (value.value) {
          if (value.value !== breed?.cropGroup?.id) {
            setValue('breed', null);
          }

          if (value.value !== crop?.cropGroup?.id) {
            setValue('crop_id', null);
          }

          setValue('production_types', [emptyProductionTypeOption]);
        }
      }
    ),
    Watch(
      Select('crop_id', {
        asyncOptions: (value, loadedOptions, { page, getValues }) => {
          const params: any = { page, value };

          const cropGroupId = getValues('crop_group_id')?.value;

          if (cropGroupId) {
            params.crop_group = cropGroupId;
          }

          return classifierService
            .valuesPaged(
              ClassifierGroup.WAREHOUSE,
              Classifier.PRODUCT_CATEGORIES_AND_TYPES,
              params
            )
            .pipe(
              map((response: Paginate<ClassifierValue>) => {
                return {
                  hasMore: response.last_page > response.current_page,
                  options: response.data.map((v) => ({
                    label: v.value,
                    value: v.id,
                    cropGroup: Object.values(v.properties)
                      .filter(
                        (p) =>
                          p.property.key === ClassifierPropertyKey.CROP_GROUP
                      )
                      .map((p) => {
                        const pv = p.value.pop();
                        return { id: pv.id, label: pv.label };
                      })
                      .pop(),
                  })),
                  additional: { page: response.current_page + 1 },
                };
              })
            );
        },
        cacheOn: ['crop_group_id'],
        multi: false,
        clearable: true,
        label: tp('crop_type'),
      }),
      ({ value, setValue, getValues, trigger }) => {
        const cropGroup = getValues('crop_group_id');
        const breed = getValues('breed');

        if (value.value) {
          if (value.cropGroup?.id !== cropGroup) {
            setValue('crop_group_id', {
              value: value.cropGroup?.id ?? '',
              label: value.cropGroup?.label ?? '',
              product_ids: [],
            });
          }

          if (value.value !== breed?.crop?.id) {
            setValue('breed', null);
          }

          setValue('production_types', [emptyProductionTypeOption]);
        }
      }
    ),
    Watch(
      Select('breed', {
        asyncOptions: (value, loadedOptions, { page, getValues }) => {
          const params: any = { page, value };
          const cropGroupId = getValues('crop_group_id')?.value;

          params.product_categories = getValues('crop_id')?.value;
          params.unique_values = true;

          return cropChangePlanService.getUniqueProducts(farmId, params).pipe(
            map((response: Paginate<Product>) => ({
              hasMore: response.last_page > response.current_page,
              options: response.data
                .map((product: Product) => ({
                  label: product.name,
                  value: product.id,
                  product: product,
                  crop: {
                    label: product.category?.value,
                    id: product.category?.id,
                  },
                  cropGroup: Object.values(product.category.properties).find(
                    (property: any) =>
                      property.property.key === ClassifierPropertyKey.CROP_GROUP
                  )?.value[0],
                }))
                .filter((value: any) => {
                  if (cropGroupId && value.cropGroup) {
                    return value.cropGroup.id === cropGroupId;
                  }

                  return value.cropGroup?.label !== 'Neattiecas';
                }),
              additional: { page: response.current_page + 1 },
            }))
          );
        },
        cacheOn: ['crop_group_id', 'crop_id'],
        multi: false,
        creatable: true,
        clearable: true,
        label: tp('crop'),
      }),
      ({ value, setValue, getValues, trigger }) => {
        const cropGroup = getValues('crop_group_id');
        const crop = getValues('crop_id');

        if (value.product) {
          if (value.cropGroup?.id !== cropGroup?.value) {
            setValue('crop_group_id', {
              value: value.cropGroup?.id ?? '',
              label: value.cropGroup?.label ?? '',
              product_ids: [],
            });
          }

          if (value.crop?.id !== crop?.value) {
            setValue('crop_id', {
              value: value.crop?.id ?? '',
              label: value.crop?.label ?? '',
              cropGroup: {
                id: value.cropGroup?.id ?? '',
                label: value.cropGroup?.label ?? '',
              },
            });
          }

          setValue('production_types', [emptyProductionTypeOption]);
        }

        trigger(['crop_group_id', 'crop_id']);
      }
    ),
    ProductionTypeSelect(
      () => [
        Disabled(
          Select('production_type', {
            asyncOptions: (value, loadedOptions, { page, getValues }) =>
              classifierService
                .valuesPaged(
                  ClassifierGroup.WAREHOUSE,
                  Classifier.PRODUCT_TYPES,
                  {
                    page,
                    value,
                    parent_id:
                      getValues('crop_id.value') ||
                      getValues('crop_group_id')?.product_ids,
                  }
                )
                .pipe(
                  map((response: Paginate<ClassifierValue>) => {
                    return {
                      hasMore: response.total === response.per_page,
                      options: response.data
                        .map((v) => ({
                          label: v.value,
                          value: v.id,
                          units:
                            Object.values(v.properties).find(
                              (property: any) =>
                                property.property?.key ===
                                ClassifierPropertyKey.UNITS_OF_MEASURE
                            )?.value ?? [],
                        }))
                        .filter((value) => {
                          return (
                            value.label !== 'Neattiecas' &&
                            !getValues('production_types').some(
                              (productionType: any) =>
                                productionType.production_type.value ===
                                value.value
                            )
                          );
                        }),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                ),
            cacheOn: ['/crop_group_id', '/crop_id'],
            multi: false,
            label: tp('planned_product'),
          }),
          {
            watchOn: '/crop_id',
            callback: (crop_id) => (crop_id?.value ? false : true),
          }
        ),
        Disabled(
          Amount(
            { amount: 'yield', unit: 'yield_unit' },
            {
              asyncOptions: async (
                value,
                loadedOptions,
                { parentName, getValues }
              ) => {
                return {
                  hasMore: false,
                  options:
                    getValues(
                      parentName
                        ? parentName.concat('.production_type')
                        : 'production_type'
                    )?.units?.map((v: string) => ({
                      label: v,
                      value: v,
                    })) ?? [],
                  additional: {},
                };
              },
              clearable: true,
              cacheOn: ['production_type'],
              label: tp('yield'),
              explanation: tp('explanation'),
              onDisabled: 'both',
            }
          ),
          {
            watchOn: 'production_type',
            callback: (production_type) =>
              production_type?.value ? false : true,
          }
        ),
      ],
      () => [
        Disabled(
          Select('production_type', {
            asyncOptions: (value, loadedOptions, { page, getValues }) =>
              classifierService
                .valuesPaged(
                  ClassifierGroup.WAREHOUSE,
                  Classifier.PRODUCT_TYPES,
                  {
                    page,
                    value,
                    parent_id:
                      getValues('crop_id.value') ||
                      getValues('crop_group_id')?.product_ids,
                  }
                )
                .pipe(
                  map((response: Paginate<ClassifierValue>) => {
                    return {
                      hasMore: response.total === response.per_page,
                      options: response.data
                        .map((v) => ({
                          label: v.value,
                          value: v.id,
                          units:
                            Object.values(v.properties).find(
                              (property: any) =>
                                property.property?.key ===
                                ClassifierPropertyKey.UNITS_OF_MEASURE
                            )?.value ?? [],
                        }))
                        .filter((value) => {
                          return (
                            value.label !== 'Neattiecas' &&
                            !getValues('production_types').some(
                              (productionType: any) =>
                                productionType.production_type.value ===
                                value.value
                            )
                          );
                        }),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                ),
            cacheOn: ['/crop_group_id', '/crop_id'],
            multi: false,
            label: tp('planned_byproduct'),
          }),
          {
            watchOn: '/crop_id',
            callback: (crop_id) => (crop_id?.value ? false : true),
          }
        ),
        Disabled(
          Amount(
            { amount: 'yield', unit: 'yield_unit' },
            {
              asyncOptions: async (
                value,
                loadedOptions,
                { parentName, getValues }
              ) => {
                return {
                  hasMore: false,
                  options:
                    getValues(
                      parentName
                        ? parentName.concat('.production_type')
                        : 'production_type'
                    )?.units?.map((v: string) => ({
                      label: v,
                      value: v,
                    })) ?? [],
                  additional: {},
                };
              },
              clearable: true,
              cacheOn: ['production_type'],
              label: tp('yield'),
              onDisabled: 'both',
            }
          ),
          {
            watchOn: 'production_type',
            callback: (production_type) =>
              production_type?.value ? false : true,
          }
        ),
      ],
      'production_types',
      tp('add_byproduct')
    ),
    TextArea('notes', tp('notes')),
    CropSelect(
      () => [
        Watch(
          Select('intercrop_crop_group_id', {
            asyncOptions: (value, loadedOptions) => {
              return cropChangePlanService
                .productCategoriesWithTypes(farmId)
                .pipe(
                  map((response: any) => ({
                    options: Object.values(response)
                      .map((v: any) => ({
                        label: v.cropGroup.label,
                        value: v.cropGroup.id,
                        product_ids: v.product_ids,
                      }))
                      .filter((value) => value.label !== 'Neattiecas'),
                  }))
                );
            },
            multi: false,
            clearable: true,
            label: tp('crop_category'),
          }),
          ({ value, setValue, getValues }) => {
            const crop = getValues('intercrop_crop_id');
            const breed = getValues('intercrop_breed');

            if (value.value) {
              if (value.value !== breed?.cropGroup?.id) {
                setValue('intercrop_breed', emptySelectOption);
              }

              if (value.value !== crop?.cropGroup?.id) {
                setValue('intercrop_crop_id', emptySelectOption);
              }
            }
          }
        ),
        Watch(
          Select('intercrop_crop_id', {
            asyncOptions: (value, loadedOptions, { page, getValues }) => {
              const params: any = { page, value };

              const cropGroupId = getValues('intercrop_crop_group_id')?.value;

              if (cropGroupId) {
                params.crop_group = cropGroupId;
              }

              return classifierService
                .valuesPaged(
                  ClassifierGroup.WAREHOUSE,
                  Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                  params
                )
                .pipe(
                  map((response: Paginate<ClassifierValue>) => {
                    return {
                      hasMore: response.total === response.per_page,
                      options: response.data.map((v) => ({
                        label: v.value,
                        value: v.id,
                        cropGroup: Object.values(v.properties)
                          .filter(
                            (p) =>
                              p.property.key ===
                              ClassifierPropertyKey.CROP_GROUP
                          )
                          .map((p) => {
                            const pv = p.value.pop();
                            return { id: pv.id, label: pv.label };
                          })
                          .pop(),
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                );
            },
            cacheOn: ['intercrop_crop_group_id'],
            multi: false,
            label: tp('crop_type'),
          }),
          ({ value, setValue, getValues }) => {
            const cropGroup = getValues('intercrop_crop_group_id');
            const breed = getValues('intercrop_breed');

            if (value.value) {
              if (value.cropGroup?.id !== cropGroup) {
                setValue('intercrop_crop_group_id', {
                  value: value.cropGroup?.id ?? '',
                  label: value.cropGroup?.label ?? '',
                });
              }

              if (value.value !== breed?.crop?.id) {
                setValue('intercrop_breed', emptySelectOption);
              }
            }
          }
        ),
        Watch(
          Select('intercrop_breed', {
            asyncOptions: (value, loadedOptions, { page, getValues }) => {
              const params: any = { page, value };
              const cropGroupId = getValues('intercrop_crop_group_id')?.value;

              params.product_categories = getValues('intercrop_crop_id')?.value;
              params.unique_values = true;

              return cropChangePlanService
                .getUniqueProducts(farmId, params)
                .pipe(
                  map((response: Paginate<Product>) => ({
                    hasMore: response.last_page > response.current_page,
                    options: response.data
                      .map((product: Product) => ({
                        label: product.name,
                        value: product.id,
                        product: product,
                        crop: {
                          label: product.category?.value,
                          id: product.category?.id,
                        },
                        cropGroup: Object.values(
                          product.category.properties
                        ).find(
                          (property: any) =>
                            property.property.key ===
                            ClassifierPropertyKey.CROP_GROUP
                        )?.value[0],
                      }))
                      .filter((value: any) => {
                        if (cropGroupId && value.cropGroup) {
                          return value.cropGroup.id === cropGroupId;
                        }

                        return value.cropGroup?.label !== 'Neattiecas';
                      }),
                    additional: { page: response.current_page + 1 },
                  }))
                );
            },
            cacheOn: ['intercrop_crop_group_id', 'intercrop_crop_id'],
            multi: false,
            creatable: true,
            clearable: true,
            label: tp('crop'),
          }),
          ({ value, setValue, getValues }) => {
            const cropGroup = getValues('intercrop_crop_group_id');
            const crop = getValues('intercrop_crop_id');

            if (value.product) {
              if (value.cropGroup?.id !== cropGroup?.value) {
                setValue('intercrop_crop_group_id', {
                  value: value.cropGroup?.id ?? '',
                  label: value.cropGroup?.label ?? '',
                  product_ids: [],
                });
              }

              if (value.crop?.id !== crop?.value) {
                setValue('intercrop_crop_id', {
                  value: value.crop?.id ?? '',
                  label: value.crop?.label ?? '',
                  cropGroup: {
                    id: value.cropGroup?.id ?? '',
                    label: value.cropGroup?.label ?? '',
                  },
                });
              }
            }
          }
        ),
      ],
      'intercrop-select',
      tp('add_intercrop'),
      tp('intercrop'),
      false
    ),
    CropSelect(
      () => [
        Watch(
          Select('interculture_crop_group_id', {
            asyncOptions: (value, loadedOptions) => {
              return cropChangePlanService
                .productCategoriesWithTypes(farmId)
                .pipe(
                  map((response: any) => ({
                    options: Object.values(response)
                      .map((v: any) => ({
                        label: v.cropGroup.label,
                        value: v.cropGroup.id,
                        product_ids: v.product_ids,
                      }))
                      .filter((value) => value.label !== 'Neattiecas'),
                  }))
                );
            },
            multi: false,
            clearable: true,
            label: tp('crop_category'),
          }),
          ({ value, setValue, getValues }) => {
            const crop = getValues('interculture_crop_id');
            const breed = getValues('interculture_breed');

            if (value.value) {
              if (value.value !== breed?.cropGroup?.id) {
                setValue('interculture_breed', emptySelectOption);
              }

              if (value.value !== crop?.cropGroup?.id) {
                setValue('interculture_crop_id', emptySelectOption);
              }
            }
          }
        ),
        Watch(
          Select('interculture_crop_id', {
            asyncOptions: (value, loadedOptions, { page, getValues }) => {
              const params: any = { page, value };

              const cropGroupId = getValues(
                'interculture_crop_group_id'
              )?.value;

              if (cropGroupId) {
                params.crop_group = cropGroupId;
              }

              return classifierService
                .valuesPaged(
                  ClassifierGroup.WAREHOUSE,
                  Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                  params
                )
                .pipe(
                  map((response: Paginate<ClassifierValue>) => {
                    return {
                      hasMore: response.total === response.per_page,
                      options: response.data.map((v) => ({
                        label: v.value,
                        value: v.id,
                        cropGroup: Object.values(v.properties)
                          .filter(
                            (p) =>
                              p.property.key ===
                              ClassifierPropertyKey.CROP_GROUP
                          )
                          .map((p) => {
                            const pv = p.value.pop();
                            return { id: pv.id, label: pv.label };
                          })
                          .pop(),
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                );
            },
            cacheOn: ['interculture_crop_group_id'],
            multi: false,
            label: tp('crop_type'),
          }),
          ({ value, setValue, getValues }) => {
            const cropGroup = getValues('interculture_crop_group_id');
            const breed = getValues('interculture_breed');

            if (value.value) {
              if (value.cropGroup?.id !== cropGroup) {
                setValue('interculture_crop_group_id', {
                  value: value.cropGroup?.id ?? '',
                  label: value.cropGroup?.label ?? '',
                });
              }

              if (value.value !== breed?.crop?.id) {
                setValue('interculture_breed', emptySelectOption);
              }
            }
          }
        ),
        Watch(
          Select('interculture_breed', {
            asyncOptions: (value, loadedOptions, { page, getValues }) => {
              const params: any = { page, value };
              const cropGroupId = getValues(
                'interculture_crop_group_id'
              )?.value;

              params.product_categories = getValues(
                'interculture_crop_id'
              )?.value;
              params.unique_values = true;

              return cropChangePlanService
                .getUniqueProducts(farmId, params)
                .pipe(
                  map((response: Paginate<Product>) => ({
                    hasMore: response.last_page > response.current_page,
                    options: response.data
                      .map((product: Product) => ({
                        label: product.name,
                        value: product.id,
                        product: product,
                        crop: {
                          label: product.category?.value,
                          id: product.category?.id,
                        },
                        cropGroup: Object.values(
                          product.category.properties
                        ).find(
                          (property: any) =>
                            property.property.key ===
                            ClassifierPropertyKey.CROP_GROUP
                        )?.value[0],
                      }))
                      .filter((value: any) => {
                        if (cropGroupId && value.cropGroup) {
                          return value.cropGroup.id === cropGroupId;
                        }

                        return value.cropGroup?.label !== 'Neattiecas';
                      }),
                    additional: { page: response.current_page + 1 },
                  }))
                );
            },
            cacheOn: ['interculture_crop_group_id', 'interculture_crop_id'],
            multi: false,
            creatable: true,
            clearable: true,
            label: tp('crop'),
          }),
          ({ value, setValue, getValues }) => {
            const cropGroup = getValues('interculture_crop_group_id');
            const crop = getValues('interculture_crop_id');

            if (value.product) {
              if (value.cropGroup?.id !== cropGroup?.value) {
                setValue('interculture_crop_group_id', {
                  value: value.cropGroup?.id ?? '',
                  label: value.cropGroup?.label ?? '',
                  product_ids: [],
                });
              }

              if (value.crop?.id !== crop?.value) {
                setValue('interculture_crop_id', {
                  value: value.crop?.id ?? '',
                  label: value.crop?.label ?? '',
                  cropGroup: {
                    id: value.cropGroup?.id ?? '',
                    label: value.cropGroup?.label ?? '',
                  },
                });
              }
            }
          }
        ),
      ],
      'interculture-select',
      tp('add_interculture'),
      tp('interculture'),
      false
    ),
  ]);

  const [footer] = useState(() => [
    Button('create'),
    Link(route(CROP_CHANGE_PLAN_INDEX, { farmId, season }), 'cancel'),
  ]);

  return (
    <Form
      title={tp('title')}
      mode="create"
      body={body}
      footer={footer}
      values={defaultValues}
      schema={schema}
      submit={submit}
      className={css['form']}
    />
  );
}

export function validateDistinctBreed(context: any) {
  const path: 'breed' | 'intercrop_breed' | 'interculture_breed' =
    context.path.toString();
  const values = {
    breed: {
      value: context.parent.breed?.value,
      crop: context.parent.crop_id?.value,
    },
    intercrop_breed: {
      value: context.parent.intercrop_breed?.value,
      crop: context.parent.intercrop_crop_id?.value,
    },
    interculture_breed: {
      value: context.parent.interculture_breed?.value,
      crop: context.parent.interculture_crop_id?.value,
    },
  };

  const { [path]: testElement, ...otherElements } = values;

  const otherElementsAllEmpty =
    Object.values(otherElements).filter((value: any) => value.value).length ===
    0;

  const hasSameValues =
    Object.values(otherElements).filter(
      (value: any) =>
        value.value === testElement.value && value.crop === testElement.crop
    ).length !== 0;

  if (!testElement || !testElement.value || otherElementsAllEmpty) {
    return true;
  }

  if (hasSameValues) {
    return context.createError({
      message: i18n.t(
        'pages.crop.crop_change_plan.create.existing_product_error'
      ),
    });
  }

  return true;
}
